<script lang="ts">
	import { cn } from "#lib/web-ui/ui/utils.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Card, CardContent, CardHeader, CardTitle } from "#lib/web-ui/ui/card/index.js";
	import { Separator } from "#lib/web-ui/ui/separator/index.js";
	import {
		Tooltip,
		TooltipContent,
		TooltipProvider,
		TooltipTrigger
	} from "#lib/web-ui/ui/tooltip/index.js";
	import InfoIcon from "@lucide/svelte/icons/info";
	import {
		InvestmentChart,
		FinalValueChart
	} from "#lib/web-ui/calculators/investment-comparison/index.js";
	import { isMobile } from "#lib/hooks/is-mobile.svelte.js";
	import { formatCurrency, decimalToPercent } from "#lib/shared/formatters.js";
	import { Section, type SectionTheme } from "#lib/web-ui/primitives/index.js";
	import {
		defaultInvestmentTypes,
		type InvestmentCalculatorProps,
		type InvestmentType
	} from "./investment-calculator.types";

	let {
		theme = "light",
		id,
		class: className,
		title = "Investment Comparison Calculator",
		subtitle = "Compare the growth of different investment types over time. Adjust the parameters to see how different investment types perform under various scenarios.",
		defaults,
		investmentTypes = defaultInvestmentTypes
	}: InvestmentCalculatorProps = $props();

	let initialInvestment = $state(defaults?.initialInvestment ?? 100_000);
	let years = $state(defaults?.years ?? 10);
	let returnRates: Record<string, number> = $state(
		Object.fromEntries(investmentTypes.map((t) => [t.key, t.defaultReturn]))
	);

	const colors = $derived(investmentTypes.map((t) => t.color));

	const chartDatasets = $derived(
		investmentTypes.map((t) => ({ label: t.label, key: t.key, color: t.color }))
	);

	const comparisonData = $derived.by(() => {
		const data: Record<string, number>[] = [];
		for (let year = 0; year <= years; year++) {
			const row: Record<string, number> = { year };
			for (const t of investmentTypes) {
				row[t.key] = initialInvestment * (1 + (returnRates[t.key] ?? 0) / 100) ** year;
			}
			data.push(row);
		}
		return data;
	});

	const finalValues = $derived.by(() => {
		if (comparisonData.length === 0 || years < 0 || years >= comparisonData.length) return [];
		const finalYearData = comparisonData[years];
		if (!finalYearData) return [];

		return investmentTypes.map((t) => ({
			label: t.label,
			initialInvestment,
			finalValue: finalYearData[t.key] ?? 0,
			totalReturn: (finalYearData[t.key] ?? 0) - initialInvestment,
			annualizedReturn: returnRates[t.key] ?? 0
		}));
	});

	function handleCurrencyInput(e: Event) {
		const target = e.target as HTMLInputElement;
		const value = Number.parseFloat(target.value.replace(/[$,]/g, ""));
		if (!isNaN(value)) initialInvestment = value;
	}

	function handlePercentInput(e: Event, key: string) {
		const target = e.target as HTMLInputElement;
		const value = Number.parseFloat(target.value.replace(/%/g, ""));
		if (!isNaN(value)) returnRates[key] = value;
	}

	function formatInputValue(value: number, isPercentage = false) {
		return isPercentage ? value.toString() : value.toLocaleString();
	}

	function handleFocus(e: FocusEvent) {
		(e.target as HTMLInputElement).select();
	}
</script>

<Section {theme} {id} class={cn(className)}>
	<h1 class="text-foreground text-5xl font-bold">{title}</h1>
	{#if subtitle}
		<p class="text-muted-foreground my-8">{subtitle}</p>
	{/if}

	<Card class="mb-8">
		<CardHeader>
			<CardTitle class="text-card-foreground">Investment Parameters</CardTitle>
		</CardHeader>
		<CardContent>
			<div class="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
				<div class="space-y-2">
					<Label for="initialInvestment" class="mb-1 items-center">
						Initial Investment
						<span class="ml-1 text-red-500">*</span>
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger>
									<span class="ml-1 inline-flex cursor-help items-center">
										<InfoIcon class="text-muted-foreground h-4 w-4" />
									</span>
								</TooltipTrigger>
								<TooltipContent>
									<p class="max-w-xs">
										The amount you plan to invest initially across all
										investment types.
									</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</Label>
					<div class="relative rounded">
						<div
							class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
						>
							<span class="text-muted-foreground">$</span>
						</div>
						<Input
							id="initialInvestment"
							type="text"
							value={formatInputValue(initialInvestment)}
							oninput={handleCurrencyInput}
							onfocus={handleFocus}
							class="pl-7"
							placeholder="e.g., 100,000"
						/>
					</div>
				</div>

				<div class="space-y-2">
					<Label for="years" class="mb-1 items-center">
						Investment Period (Years)
						<span class="ml-1 text-red-500">*</span>
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger>
									<span class="ml-1 inline-flex cursor-help items-center">
										<InfoIcon class="text-muted-foreground h-4 w-4" />
									</span>
								</TooltipTrigger>
								<TooltipContent>
									<p class="max-w-xs">
										The length of time you plan to hold your investments.
									</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</Label>
					<div class="relative">
						<Input
							id="years"
							type="number"
							min="1"
							max="50"
							bind:value={years}
							onfocus={handleFocus}
						/>
						<span
							class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
							>yrs</span
						>
					</div>
				</div>
			</div>

			<Separator class="my-6" />

			<div class="mb-4 flex items-center">
				<h2 class="text-foreground text-lg font-medium">Annual Return Rates</h2>
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>
							<span class="ml-2 inline-flex cursor-help items-center">
								<InfoIcon class="text-muted-foreground h-4 w-4" />
							</span>
						</TooltipTrigger>
						<TooltipContent>
							<p class="max-w-xs">
								Expected annual returns for each investment type. Adjust these
								values based on your market expectations.
							</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</div>

			<div
				class="grid grid-cols-1 gap-6 md:grid-cols-2"
				style:grid-template-columns="repeat(auto-fit, minmax(200px, 1fr))"
			>
				{#each investmentTypes as type}
					<div class="bg-card text-card-foreground rounded border p-4">
						<div class="mb-2 flex items-center">
							<span
								class="mr-2 h-3 w-3 rounded-full"
								style:background-color={type.color}
							></span>
							<p class="font-semibold">{type.label}</p>
						</div>
						<div class="relative">
							<Input
								type="text"
								value={formatInputValue(returnRates[type.key] ?? 0, true)}
								oninput={(e: Event) => handlePercentInput(e, type.key)}
								onfocus={handleFocus}
								min="0"
								max="30"
								step="0.1"
							/>
							<span
								class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
								>%</span
							>
						</div>
					</div>
				{/each}
			</div>
		</CardContent>
	</Card>

	<Card class="mb-8">
		<CardHeader>
			<CardTitle class="text-card-foreground text-center"
				>Investment Growth Over Time</CardTitle
			>
		</CardHeader>
		<CardContent>
			<InvestmentChart data={comparisonData} datasets={chartDatasets} />
		</CardContent>
	</Card>

	<Card class="mb-8">
		<CardHeader>
			<CardTitle class="text-card-foreground text-center"
				>Final Investment Value Comparison</CardTitle
			>
		</CardHeader>
		<CardContent>
			<FinalValueChart data={finalValues} {colors} />
		</CardContent>
	</Card>

	<Card>
		<CardHeader>
			<CardTitle class="text-card-foreground text-center">Investment Summary</CardTitle>
		</CardHeader>
		<CardContent>
			{#if isMobile.value}
				<div class="space-y-4">
					{#each finalValues as item, i}
						<div class="overflow-hidden rounded border">
							<div class="bg-muted flex items-center border-b p-3">
								<span
									class="mr-2 inline-block h-3 w-3 rounded-full"
									style:background-color={colors[i]}
								></span>
								<span class="text-foreground font-medium">{item.label}</span>
							</div>
							<div class="grid grid-cols-2 gap-2 p-3">
								<div>
									<div class="text-muted-foreground text-sm">
										Initial Investment
									</div>
									<div class="text-card-foreground font-medium">
										{formatCurrency(item.initialInvestment)}
									</div>
								</div>
								<div>
									<div class="text-muted-foreground text-sm">Final Value</div>
									<div class="text-card-foreground font-medium">
										{formatCurrency(item.finalValue)}
									</div>
								</div>
								<div>
									<div class="text-muted-foreground text-sm">Total Return</div>
									<div class="text-card-foreground font-medium">
										{formatCurrency(item.totalReturn)}
									</div>
								</div>
								<div>
									<div class="text-muted-foreground text-sm">
										Annualized Return
									</div>
									<div class="text-card-foreground font-medium">
										{decimalToPercent(item.annualizedReturn)}
									</div>
								</div>
							</div>
						</div>
					{:else}
						<div class="text-muted-foreground py-4 text-center">
							Enter valid parameters to see summary.
						</div>
					{/each}
				</div>
			{:else}
				<div class="overflow-x-auto">
					<table class="w-full">
						<thead>
							<tr class="border-b">
								<th class="text-foreground px-3 py-2 text-left">Investment Type</th>
								<th class="text-foreground px-3 py-2 text-right">
									Initial Investment
								</th>
								<th class="text-foreground px-3 py-2 text-right">Final Value</th>
								<th class="text-foreground px-3 py-2 text-right">Total Return</th>
								<th class="text-foreground px-3 py-2 text-right">
									Annualized Return
								</th>
							</tr>
						</thead>
						<tbody>
							{#each finalValues as item, i}
								<tr class="border-b">
									<td class="px-3 py-3">
										<div class="flex items-center">
											<span
												class="mr-2 inline-block h-3 w-3 rounded-full"
												style:background-color={colors[i]}
											></span>
											<span class="text-foreground font-medium"
												>{item.label}</span
											>
										</div>
									</td>
									<td class="text-foreground px-3 py-3 text-right"
										>{formatCurrency(item.initialInvestment)}</td
									>
									<td class="text-foreground px-3 py-3 text-right"
										>{formatCurrency(item.finalValue)}</td
									>
									<td class="text-foreground px-3 py-3 text-right"
										>{formatCurrency(item.totalReturn)}</td
									>
									<td class="text-foreground px-3 py-3 text-right"
										>{decimalToPercent(item.annualizedReturn)}</td
									>
								</tr>
							{:else}
								<tr>
									<td colspan={5} class="text-muted-foreground py-4 text-center"
										>Enter valid parameters to see summary.</td
									>
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
			{/if}
		</CardContent>
	</Card>
</Section>
